// @ts-nocheck
/**
 * Utility functions to replace jQuery and Lodash dependencies
 */

import DOMPurify from 'dompurify';

/**
 * Check if an object is empty
 * Replaces jQuery's isEmptyObject and Lodash isEmpty
 * @param {*} obj - The object to check
 * @returns {boolean} - True if object is null, undefined, or has no own properties
 */
export function isEmptyObject(obj) {
    if (obj == null) return true;
    if (typeof obj !== 'object') return false;
    return Object.keys(obj).length === 0;
}

// u/s/code: Salesforce stores them in rich agent replies (ACOP-123, AC-4). data-copilot-doc-id on
// an <img> survives through DOMPurify's default ALLOW_DATA_ATTR; FeedBody loads that image itself.
const SANITIZE_OPTIONS = {
    ALLOWED_TAGS: ['a', 'br', 'p', 'b', 'strong', 'i', 'em', 'u', 's', 'code', 'ul', 'ol', 'li', 'img'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'class', 'width', 'height', 'title'],
};

function isSafeImgSrc(src) {
    if (!src || typeof src !== 'string') return false;
    if (/^https?:\/\//i.test(src.trim())) return true;
    return /^data:image\/(png|jpe?g|gif|webp|bmp|svg\+xml);base64,/i.test(src.trim());
}

/**
 * Sanitize a Chatter comment body for safe HTML rendering.
 * Replaces literal \n with <br>, sanitizes HTML via DOMPurify,
 * and ensures links open in a new tab. Inline images use http(s) or safe data-URIs only.
 */
export function sanitizeCommentBody(raw) {
    if (!raw) return '';
    let html = raw.replace(/\\n/g, '<br>');
    if (!/<[a-z][\s\S]*>/i.test(html)) {
        html = html.replace(/\n/g, '<br>');
    }
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
        if (node.tagName === 'A') {
            node.setAttribute('target', '_blank');
            node.setAttribute('rel', 'noopener noreferrer');
        }
        if (node.tagName === 'IMG') {
            const src = node.getAttribute('src');
            if (!isSafeImgSrc(src)) {
                node.removeAttribute('src');
            }
        }
    });
    const clean = DOMPurify.sanitize(html, SANITIZE_OPTIONS);
    DOMPurify.removeHook('afterSanitizeAttributes');
    return clean;
}

/**
 * Controls that collect input. DOMPurify keeps them by default, and a form inside a page the reader trusts is a
 * believable fake sign-in, so a rich-text field loses them (ACOP-158). Their words stay.
 */
const RICH_TEXT_FORBID_TAGS = ['form', 'input', 'button', 'select', 'textarea'];

/**
 * Sanitize a Salesforce rich-text field (Early Adopters Requirements / Summary) for rendering as HTML.
 *
 * Keeps the formatting such a field holds (paragraphs, emphasis, headings, lists, tables, links, inline style).
 * DOMPurify's defaults already remove script, handlers, frames, objects and `javascript:`/`data:` addresses; on
 * top of that the controls in RICH_TEXT_FORBID_TAGS go. Anything that is not text gives an empty string.
 */
export function sanitizeRichText(raw: unknown): string {
    if (!raw || typeof raw !== 'string') return '';
    return DOMPurify.sanitize(raw, { FORBID_TAGS: RICH_TEXT_FORBID_TAGS });
}

const CONVERSATION_FEED_TYPES = new Set(['TextPost', 'ContentPost', 'LinkPost']);

/**
 * True for Chatter feed items that are system/activity rows (e.g. TrackedChange)
 * rather than normal conversation posts. Copilot still shows AllUsers items of
 * every type so replies emailed to customers remain visible.
 */
export function isSystemFeedItem(type) {
    if (!type || typeof type !== 'string') return false;
    return !CONVERSATION_FEED_TYPES.has(type);
}

